import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Avatar,
  LinearProgress,
  Fade,
  Slide,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Stack,
  Badge,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  alpha,
  useTheme,
} from '@mui/material';
import {
  People,
  Inventory,
  Receipt,
  TrendingUp,
  Warning,
  CheckCircle,
  AttachMoney,
  ShoppingCart,
  Diamond,
  Business,
  Assessment,
  Refresh,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  Add,
  Timeline,
  PieChart,
  BarChart,
  ShowChart,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCustomers } from '../redux/actions/customerActions';
import { getProducts, getLowStockProducts } from '../redux/actions/productActions';
import { getInvoices, getSalesSummary } from '../redux/actions/invoiceActions';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  const { customers, pagination: customerPagination } = useSelector((state) => state.customers);
  const { products, lowStockProducts, pagination: productPagination } = useSelector((state) => state.products);
  const { invoices, pagination: invoicePagination, salesSummary } = useSelector((state) => state.invoices);

  const [loading, setLoading] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [chartTab, setChartTab] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, [dispatch]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
    // Load dashboard data
      await Promise.all([
        dispatch(getCustomers({ page: 1, limit: 5 })),
        dispatch(getProducts({ page: 1, limit: 5 })),
        dispatch(getInvoices({ page: 1, limit: 5 })),
        dispatch(getLowStockProducts(10)),
      ]);
    
    // Load sales summary for current month, including today
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const today = new Date();
    // Use today as end date if it's within the current month, otherwise use last day of month
    const lastDay = today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear()
      ? today
      : new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
      await dispatch(getSalesSummary({
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0],
    }));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setAnimationKey(prev => prev + 1);
    loadDashboardData();
  };

  const getTotalSales = () => {
    if (!salesSummary || salesSummary.length === 0) return 0;
    return salesSummary.reduce((total, day) => total + parseFloat(day.total_grand_total || 0), 0);
  };

  const getTodaySales = () => {
    if (!salesSummary || salesSummary.length === 0) return 0;
    const today = new Date().toISOString().split('T')[0];
    // Check both 'date' and 'invoice_date' fields since the API might return either
    const todayData = salesSummary.find(day => {
      const dayDate = day.date || day.invoice_date;
      return dayDate && dayDate.split('T')[0] === today;
    });
    return todayData ? parseFloat(todayData.total_grand_total || 0) : 0;
  };

  const getGrowthRate = () => {
    if (!salesSummary || salesSummary.length < 2) return 0;
    const currentMonth = getTotalSales();
    const previousMonth = currentMonth * 0.85; // Mock data for demonstration
    return ((currentMonth - previousMonth) / previousMonth) * 100;
  };

  // Chart data processing functions
  const getSalesChartData = () => {
    if (!salesSummary || salesSummary.length === 0) return [];
    
    return salesSummary.map((day, index) => ({
      date: new Date(day.invoice_date || day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      fullDate: day.invoice_date || day.date,
      sales: parseFloat(day.total_grand_total || 0),
      invoices: parseInt(day.invoice_count || 0),
      cgst: parseFloat(day.total_cgst || 0),
      sgst: parseFloat(day.total_sgst || 0),
      igst: parseFloat(day.total_igst || 0),
      subtotal: parseFloat(day.total_subtotal || 0),
    })).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  };

  const getPieChartData = () => {
    if (!salesSummary || salesSummary.length === 0) return [];
    
    const totalCgst = salesSummary.reduce((sum, day) => sum + parseFloat(day.total_cgst || 0), 0);
    const totalSgst = salesSummary.reduce((sum, day) => sum + parseFloat(day.total_sgst || 0), 0);
    const totalIgst = salesSummary.reduce((sum, day) => sum + parseFloat(day.total_igst || 0), 0);
    const totalSubtotal = salesSummary.reduce((sum, day) => sum + parseFloat(day.total_subtotal || 0), 0);
    
    return [
      { name: 'Sub Total', value: totalSubtotal, color: '#4caf50' },
      { name: 'CGST', value: totalCgst, color: '#2196f3' },
      { name: 'SGST', value: totalSgst, color: '#ff9800' },
      { name: 'IGST', value: totalIgst, color: '#f44336' },
    ].filter(item => item.value > 0);
  };

  const getWeeklyData = () => {
    if (!salesSummary || salesSummary.length === 0) return [];
    
    // Group data by week
    const weeklyData = {};
    salesSummary.forEach(day => {
      const date = new Date(day.invoice_date || day.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          week: `Week ${Math.ceil(date.getDate() / 7)}`,
          sales: 0,
          invoices: 0,
        };
      }
      
      weeklyData[weekKey].sales += parseFloat(day.total_grand_total || 0);
      weeklyData[weekKey].invoices += parseInt(day.invoice_count || 0);
    });
    
    return Object.values(weeklyData).sort((a, b) => a.week.localeCompare(b.week));
  };

  const handleChartTabChange = (event, newValue) => {
    setChartTab(newValue);
  };

  const statsCards = [
    {
      title: 'Total Revenue',
      value: `₹${getTotalSales().toLocaleString()}`,
      subtitle: 'This Month',
      icon: <AttachMoney />,
      color: '#4caf50',
      trend: getGrowthRate(),
      trendLabel: 'vs last month',
      action: () => navigate('/invoices'),
    },
    {
      title: 'Today\'s Sales',
      value: `₹${getTodaySales().toLocaleString()}`,
      subtitle: 'Today',
      icon: <TrendingUp />,
      color: '#2196f3',
      trend: 12.5,
      trendLabel: 'vs yesterday',
      action: () => navigate('/invoices'),
    },
    {
      title: 'Total Customers',
      value: customerPagination.totalItems || 0,
      subtitle: 'Active customers',
      icon: <People />,
      color: '#9c27b0',
      trend: 8.2,
      trendLabel: 'vs last month',
      action: () => navigate('/customers'),
    },
    {
      title: 'Total Products',
      value: productPagination.totalItems || 0,
      subtitle: 'In inventory',
      icon: <Inventory />,
      color: '#ff9800',
      trend: 5.1,
      trendLabel: 'vs last month',
      action: () => navigate('/products'),
    },
    {
      title: 'Total Invoices',
      value: invoicePagination.totalItems || 0,
      subtitle: 'This month',
      icon: <Receipt />,
      color: '#f44336',
      trend: 15.3,
      trendLabel: 'vs last month',
      action: () => navigate('/invoices'),
    },
    {
      title: 'Low Stock Alert',
      value: lowStockProducts.length || 0,
      subtitle: 'Need attention',
      icon: <Warning />,
      color: '#ff5722',
      trend: -2.1,
      trendLabel: 'vs last week',
      action: () => navigate('/products'),
    },
  ];

  const chartData = getSalesChartData();
  const pieData = getPieChartData();

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
    <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                Welcome back, {user?.username}! 👋
      </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
                Here's what's happening with your jewellery business today
      </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh Data">
                <IconButton 
                  onClick={handleRefresh} 
                  disabled={loading}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    }
                  }}
                >
                  <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Analytics">
                <IconButton 
                  onClick={() => navigate('/reports')}
                  sx={{ 
                    background: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                    '&:hover': {
                      background: 'rgba(102, 126, 234, 0.2)',
                    }
                  }}
                >
                  <Assessment />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Receipt />}
              onClick={() => navigate('/invoices')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                },
              }}
            >
              Manage Invoices
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/billing')}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                },
              }}
            >
              New Invoice
            </Button>
            <Button
              variant="outlined"
              startIcon={<People />}
              onClick={() => navigate('/customers')}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#5a6fd8',
                  background: 'rgba(102, 126, 234, 0.04)',
                },
              }}
            >
              Add Customer
            </Button>
            <Button
              variant="outlined"
              startIcon={<Inventory />}
              onClick={() => navigate('/products')}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#f57c00',
                  background: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Manage Products
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={4} xl={2} key={index}>
            <Fade in timeout={800 + index * 100}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                    '& .stat-icon': {
                      transform: 'scale(1.1)',
                    },
                  },
                }}
                onClick={stat.action}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar
                      className="stat-icon"
                    sx={{
                        width: 56,
                        height: 56,
                        background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}CC 100%)`,
                        boxShadow: `0 8px 25px ${stat.color}40`,
                        transition: 'transform 0.3s ease',
                    }}
                  >
                    {stat.icon}
                    </Avatar>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.5 }}>
                        {stat.trend > 0 ? (
                          <ArrowUpward sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
                        ) : (
                          <ArrowDownward sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: stat.trend > 0 ? '#4caf50' : '#f44336',
                            fontWeight: 600,
                          }}
                        >
                          {Math.abs(stat.trend).toFixed(1)}%
                        </Typography>
                      </Box>
                      <Typography component="span" variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                        {stat.trendLabel}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'text.primary' }}>
                    {stat.value}
                  </Typography>
                  <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {stat.title}
                </Typography>
                  <Typography component="span" variant="caption" color="text.secondary" display="block">
                    {stat.subtitle}
                </Typography>
              </CardContent>
            </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Invoices */}
        <Grid item xs={12} lg={6}>
          <Slide direction="up" in timeout={1000}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Receipt sx={{ mr: 1, color: '#667eea' }} />
              Recent Invoices
            </Typography>
                    <Button
                      size="small"
                      endIcon={<Visibility />}
                      onClick={() => navigate('/invoices')}
                      sx={{ textTransform: 'none' }}
                    >
                      View All
                    </Button>
                  </Box>
                  <Divider />
                </Box>
                
                <List sx={{ p: 0 }}>
                  {invoices.slice(0, 5).map((invoice, index) => (
                    <Fade in timeout={1200 + index * 100} key={invoice.id}>
                      <ListItem
                        sx={{
                          px: 3,
                          py: 2,
                          '&:hover': {
                            background: 'rgba(102, 126, 234, 0.04)',
                          },
                        }}
                      >
                  <ListItemIcon>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              fontSize: '0.9rem',
                            }}
                          >
                            {invoice.invoice_no?.slice(-2) || '00'}
                          </Avatar>
                  </ListItemIcon>
                  <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {invoice.invoice_no}
                            </Typography>
                          }
                          secondary={
                            <Typography component="span" variant="body2" color="text.secondary" display="block">
                              {invoice.customer?.name || 'Walk-in Customer'}
                              <br />
                              <Typography component="span" variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                ₹{parseFloat(invoice.grand_total || 0).toLocaleString()}
                              </Typography>
                            </Typography>
                          }
                        />
                        <Box sx={{ textAlign: 'right' }}>
                  <Chip
                    label={new Date(invoice.invoice_date).toLocaleDateString()}
                    size="small"
                    variant="outlined"
                            sx={{ mb: 1 }}
                  />
                          <Typography variant="caption" color="text.secondary" display="block">
                            {new Date(invoice.invoice_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                </ListItem>
                    </Fade>
              ))}
              {invoices.length === 0 && (
                <ListItem>
                      <ListItemIcon>
                        <Receipt color="disabled" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="No recent invoices" 
                        secondary="Create your first invoice to get started"
                      />
                </ListItem>
              )}
            </List>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Low Stock Alert */}
        <Grid item xs={12} lg={6}>
          <Slide direction="up" in timeout={1200}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                      <Warning sx={{ mr: 1, color: '#ff5722' }} />
              Low Stock Alert
                      {lowStockProducts.length > 0 && (
                        <Badge badgeContent={lowStockProducts.length} color="error" sx={{ ml: 1 }}>
                          <Box />
                        </Badge>
                      )}
            </Typography>
                    <Button
                      size="small"
                      endIcon={<Inventory />}
                      onClick={() => navigate('/products')}
                      sx={{ textTransform: 'none' }}
                    >
                      Manage
                    </Button>
                  </Box>
                  <Divider />
                </Box>
                
                <List sx={{ p: 0 }}>
                  {lowStockProducts.slice(0, 5).map((product, index) => (
                    <Fade in timeout={1400 + index * 100} key={product.id}>
                      <ListItem
                        sx={{
                          px: 3,
                          py: 2,
                          '&:hover': {
                            background: 'rgba(255, 87, 34, 0.04)',
                          },
                        }}
                      >
                  <ListItemIcon>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #ff5722 0%, #f44336 100%)',
                            }}
                          >
                            <Inventory />
                          </Avatar>
                  </ListItemIcon>
                  <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                          }
                          secondary={
                            <Typography component="span" variant="body2" color="text.secondary" display="block">
                              {product.category}
                              <br />
                              <Typography component="span" variant="body2" color="error" sx={{ fontWeight: 600 }}>
                                Stock: {product.stock_qty} units
                              </Typography>
                            </Typography>
                          }
                  />
                  <Chip
                          label="Low Stock"
                    size="small"
                          color="error"
                    variant="outlined"
                  />
                </ListItem>
                    </Fade>
              ))}
              {lowStockProducts.length === 0 && (
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                      <ListItemText 
                        primary="All products are well stocked" 
                        secondary="No low stock alerts at the moment"
                      />
                </ListItem>
              )}
            </List>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Enhanced Sales Analytics Chart Section */}
        <Grid item xs={12}>
          <Slide direction="up" in timeout={1400}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(102, 126, 234, 0.1)',
                borderRadius: 4,
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Chart Header */}
                <Box sx={{ 
                  p: 3, 
                  pb: 2,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Assessment sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        Sales Analytics
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        icon={<Timeline />}
                        label="This Month"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        icon={<TrendingUp />}
                        label={`+${getGrowthRate().toFixed(1)}% Growth`}
                        color="success"
                        sx={{ 
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                          color: 'white'
                        }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', ml: 1 }}>
                        ₹{getTotalSales().toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Chart Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                  <Tabs 
                    value={chartTab} 
                    onChange={handleChartTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 56,
                        fontSize: '0.95rem',
                        px: 3,
                      },
                      '& .Mui-selected': {
                        color: 'primary.main',
                      },
                      '& .MuiTabs-indicator': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                      },
                    }}
                  >
                    <Tab 
                      icon={<ShowChart sx={{ fontSize: 20 }} />} 
                      iconPosition="start"
                      label="Sales Trend" 
                    />
                    <Tab 
                      icon={<BarChart sx={{ fontSize: 20 }} />} 
                      iconPosition="start"
                      label="Daily Sales" 
                    />
                    <Tab 
                      icon={<PieChart sx={{ fontSize: 20 }} />} 
                      iconPosition="start"
                      label="Tax Breakdown" 
                    />
                  </Tabs>
                </Box>

                {/* Chart Content */}
                <Box sx={{ p: 4, minHeight: 400 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                      <CircularProgress size={48} />
                    </Box>
                  ) : chartData.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: 400,
                      textAlign: 'center'
                    }}>
                      <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Sales Data Available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create invoices to see sales analytics and trends
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/billing')}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Create First Invoice
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {chartTab === 0 && (
                        <Box sx={{ height: 400 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                              <defs>
                                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#667eea" stopOpacity={0.4}/>
                                  <stop offset="50%" stopColor="#667eea" stopOpacity={0.2}/>
                                  <stop offset="95%" stopColor="#667eea" stopOpacity={0.05}/>
                                </linearGradient>
                                <linearGradient id="invoiceGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3}/>
                                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.05}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                              <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                              />
                              <YAxis 
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  name === 'sales' ? `₹${value.toLocaleString()}` : `${value} invoices`,
                                  name === 'sales' ? 'Sales' : 'Invoice Count'
                                ]}
                                labelFormatter={(label) => `Date: ${label}`}
                                contentStyle={{
                                  background: 'rgba(255, 255, 255, 0.98)',
                                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                  borderRadius: 12,
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                  padding: '12px',
                                }}
                                cursor={{ stroke: '#667eea', strokeWidth: 2, strokeDasharray: '5 5' }}
                              />
                              <Area
                                type="monotone"
                                dataKey="sales"
                                stroke="#667eea"
                                strokeWidth={3}
                                fill="url(#salesGradient)"
                                dot={{ fill: '#667eea', strokeWidth: 2, r: 5 }}
                                activeDot={{ r: 7, stroke: '#667eea', strokeWidth: 2, fill: '#fff' }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      )}

                      {chartTab === 1 && (
                        <Box sx={{ height: 400 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                              <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                              />
                              <YAxis 
                                yAxisId="sales"
                                orientation="left"
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                              />
                              <YAxis 
                                yAxisId="invoices"
                                orientation="right"
                                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                                axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                                tickLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  name === 'sales' ? `₹${value.toLocaleString()}` : `${value} invoices`,
                                  name === 'sales' ? 'Sales (₹)' : 'Invoice Count'
                                ]}
                                labelFormatter={(label) => `Date: ${label}`}
                                contentStyle={{
                                  background: 'rgba(255, 255, 255, 0.98)',
                                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                  borderRadius: 12,
                                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                  padding: '12px',
                                }}
                                cursor={{ stroke: '#667eea', strokeWidth: 2, strokeDasharray: '5 5' }}
                              />
                              <Legend 
                                wrapperStyle={{ paddingTop: '20px' }}
                                iconType="circle"
                              />
                              <Bar 
                                yAxisId="sales"
                                dataKey="sales" 
                                fill="url(#barGradient)"
                                name="Sales (₹)"
                                radius={[8, 8, 0, 0]}
                              >
                                {chartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={`hsl(${220 + index * 5}, 70%, 60%)`} />
                                ))}
                              </Bar>
                              <defs>
                                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.9}/>
                                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.6}/>
                                </linearGradient>
                              </defs>
                              <Line 
                                yAxisId="invoices"
                                type="monotone" 
                                dataKey="invoices" 
                                stroke="#ff9800" 
                                strokeWidth={3}
                                name="Invoice Count"
                                dot={{ fill: '#ff9800', strokeWidth: 2, r: 5 }}
                                activeDot={{ r: 7, stroke: '#ff9800', strokeWidth: 2, fill: '#fff' }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                      )}

                      {chartTab === 2 && (
                        <Box sx={{ height: 400 }}>
                          {pieData.length === 0 ? (
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              height: '100%',
                              textAlign: 'center'
                            }}>
                              <PieChart sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                No Tax Data Available
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Tax breakdown will appear here once you have invoice data
                              </Typography>
                            </Box>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart>
                                <defs>
                                  {pieData.map((entry, index) => (
                                    <linearGradient key={`gradient-${index}`} id={`pieGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor={entry.color} stopOpacity={0.9}/>
                                      <stop offset="95%" stopColor={entry.color} stopOpacity={0.6}/>
                                    </linearGradient>
                                  ))}
                                </defs>
                                <Pie
                                  data={pieData}
                                  cx="50%"
                                  cy="45%"
                                  labelLine={false}
                                  label={({ name, percent, value }) => 
                                    `${name}\n${(percent * 100).toFixed(1)}%\n₹${value.toLocaleString()}`
                                  }
                                  outerRadius={100}
                                  innerRadius={40}
                                  fill="#8884d8"
                                  dataKey="value"
                                  paddingAngle={2}
                                >
                                  {pieData.map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={`url(#pieGradient-${index})`}
                                      stroke={entry.color}
                                      strokeWidth={2}
                                    />
                                  ))}
                                </Pie>
                                <RechartsTooltip 
                                  formatter={(value, name) => [
                                    `₹${value.toLocaleString()}`,
                                    name
                                  ]}
                                  contentStyle={{
                                    background: 'rgba(255, 255, 255, 0.98)',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                    borderRadius: 12,
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                    padding: '12px',
                                  }}
                                />
                                <Legend 
                                  verticalAlign="bottom" 
                                  height={60}
                                  formatter={(value, entry) => (
                                    <Box component="span" sx={{ 
                                      color: entry.color, 
                                      fontWeight: 600,
                                      fontSize: '0.875rem'
                                    }}>
                                      {value}
                                    </Box>
                                  )}
                                  iconType="circle"
                                />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>

                {/* Chart Footer Actions */}
                <Box sx={{ 
                  p: 2, 
                  pt: 0,
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<ShowChart />}
                    onClick={() => navigate('/reports')}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'primary.dark',
                        background: alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                  >
                    View Detailed Reports
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<BarChart />}
                    onClick={() => navigate('/invoices')}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 2,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        boxShadow: 4,
                      }
                    }}
                  >
                    View All Invoices
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading dashboard data...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
